import { PaymentWhereInput, Query, QueryPaymentsArgs } from '../../__generated__/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EntityEnum } from '../../@types/client/DashboardUserRolePermissions';
import GET_PAYMENT_COUNT from '../../graphql/aggregatePayment';
import { GET_PAYMENT_TABLE_DATA } from '../../graphql/getPaymentTableData';
import OrderByParams from '../../@types/client/OrderByParams';
import PAYMENTS_TABLE_HEADERS from './constants/PaymentsTableHeaders';
import PAYMENTS_TABLE_SEARCH_FIELDS from './constants/PaymentsTableSearchFields';
import { RootState } from '../../redux/reducers';
import SearchField from '../../@types/SearchField';
import TableHookData from '../@types/TableHookData';
import TableHookProps from '../@types/TableHookProps';
import generateMembershipQuery from '../../util/api/generateMembershipQuery';
import generateNestedMembershipQuery from '../../util/api/generateNestedMembershipQuery';
import generatePaymentsTableData from './functions/generatePaymentsTableData';
import parseOrderByDataTableParams from '../../components/DataTable/util/parseOrderByDataTableParams';
import useGetMany from '../useGetMany';
import useRemovePayment from '../useRemovePayment';
import { useSelector } from 'react-redux';

type PaymentQueryParams = {
  orderBy?: OrderByParams;
  where: Partial<PaymentWhereInput>;
};

type GetPaymentTableData = Pick<Query, 'payments'>;
type AggregatePaymentQueryData = Pick<Query, 'aggregatePayment'>;

export default function usePaymentsTableData({
  dataTableKey = 'payments',
  formName = 'payment',
  headers = PAYMENTS_TABLE_HEADERS,
  isSearchable = true,
  queryOptions,
  searchFields = PAYMENTS_TABLE_SEARCH_FIELDS,
}: TableHookProps<QueryPaymentsArgs, PaymentWhereInput>): TableHookData<PaymentWhereInput> {
  const membership = useSelector((state: RootState) => state.me.activeMembership);
  const options = useRef<Partial<QueryPaymentsArgs>>(queryOptions ?? {});
  const sortOptions = useSelector((state: RootState) => state.dataTable.sortOptions);

  const [searchParameters, setSearchParameters] = useState<PaymentWhereInput>({});

  const { data, error, loading, offset, query, setOffset } = useGetMany<
    GetPaymentTableData,
    QueryPaymentsArgs
  >({
    graphqlQuery: GET_PAYMENT_TABLE_DATA,
  });

  const {
    data: countData,
    error: countError,
    loading: countLoading,
    query: countQuery,
  } = useGetMany<AggregatePaymentQueryData, QueryPaymentsArgs>({
    graphqlQuery: GET_PAYMENT_COUNT,
  });

  const createParams = useCallback((): PaymentQueryParams => {
    const sortParams = parseOrderByDataTableParams(sortOptions[dataTableKey]);

    return {
      ...(sortParams && { orderBy: sortParams }),
      where: {
        user: { is: { ...generateNestedMembershipQuery(membership) } },
        ...generateMembershipQuery(membership),
        ...options.current?.where,
        ...searchParameters,
      },
    };
  }, [membership, options, searchParameters, sortOptions, dataTableKey]);

  const get = useCallback(() => {
    const params = createParams();

    query({
      ...options.current,
      ...(params.orderBy && { orderBy: params.orderBy }),
      skip: options.current?.skip ?? offset,
      take: options.current?.take ?? 10,
      where: params.where,
    });
  }, [createParams, offset, query]);

  const getCount = useCallback(() => {
    const params = createParams();

    countQuery({
      where: params.where,
    });
  }, [countQuery, createParams]);

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    getCount();
  }, [getCount]);

  return {
    count: countData?.aggregatePayment?._count?._all ?? 0,
    data: generatePaymentsTableData(data),
    dataTableKey,
    dataType: 'Payment',
    error: error || countError,
    formName,
    headers,
    isLoading: loading || countLoading,
    isSearchable,
    offset,
    permission: EntityEnum.NONE,
    remove: useRemovePayment,
    search: (searchField: SearchField<PaymentWhereInput>, searchValue: string): void => {
      setSearchParameters({
        [searchField.queryField]: searchField.transform(searchValue),
      });

      setOffset(0);
    },
    searchFields,
    setOffset,
  };
}
